import React, { useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setAppBarHeight, setModal, setActiveMenu } from "../../Modules/Common/App";
import { AccountMenu } from "../Menu/AccountMenu";
import { SearchMenu } from "../Menu/SearchMenu";
import { CategoryMenu } from "../Menu/CategoryMenu";
import { AnimatedAppButton } from "./AnimatedAppButton";
import { TOOLS_CONFIG } from "../../Setup/Config/tools-config";
import { Container, Stack, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme, Typography, Avatar, Box as MuiBox, Collapse } from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import AppsIcon from "@mui/icons-material/Apps";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import CodeIcon from "@mui/icons-material/CodeOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import CalculateIcon from "@mui/icons-material/CalculateOutlined";
import WidgetsIcon from "@mui/icons-material/WidgetsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useThemeMode } from "../../Providers/ThemeProvider";
import { getUserAvatar, getUserInitials } from "../../Setup/Utility/getUserAvatar";
import { handleLogout } from "../../Modules/Common/Auth";
import { useNavigate } from "react-router-dom";

const StyledThemeToggle = styled(IconButton)(({ theme }) => ({
    width: 40,
    height: 40,
    color: theme.palette.text.secondary,
    position: 'relative',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.text.secondary}`,
    backgroundColor: 'transparent',
    transition: theme.transitions.create(['all'], {
        duration: theme.transitions.duration.short,
    }),
    overflow: 'hidden',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
    },
    '& .MuiSvgIcon-root': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '1.25rem',
        transition: theme.transitions.create(['all'], {
            duration: theme.transitions.duration.short,
        }),
    },
}));

// Mobile Menu Content Component (Full Screen like SearchMenu)
const MobileMenuContent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { mode, toggleMode } = useThemeMode();
    const auth = useSelector((store) => store.auth);
    const { isLoggedIn, user } = auth;

    const handleClose = () => {
        dispatch(setModal({ component: null, open: false }));
    };

    const handleThemeToggle = () => {
        toggleMode();
        dispatch(setActiveMenu('theme-toggle'));
    };

    const handleLogoutClick = () => {
        dispatch(setActiveMenu('logout'));
        handleLogout(dispatch);
        handleClose();
        navigate('/');
    };

    const avatarUrl = getUserAvatar(user, user?.avatar_url);
    const userInitials = getUserInitials(user);
    const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    const userEmail = user?.email || '';
    
    // Category menu states
    const [openCategories, setOpenCategories] = React.useState({});
    
    const handleCategoryToggle = (category) => {
        setOpenCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };
    
    // Get tools by category
    const getCategoryTools = (category, maxItems = 5, isMisc = false) => {
        if (isMisc) {
            // For General menu, get high-value tools from URL Tools, Text Processing, and Design Tools
            const miscCategories = ['URL Tools', 'Text Processing', 'Design Tools'];
            const highValueToolIds = [
                'url-encoder-decoder', // Featured URL tool
                'base64-encoder-decoder', // Featured Text tool
                'color-picker', // Featured Design tool
                'url-parser', // Popular URL tool
                'text-case-converter', // Popular Text tool
                'color-converter', // Popular Design tool
            ];
            
            return TOOLS_CONFIG
                .filter(tool => 
                    (miscCategories.includes(tool.category) || highValueToolIds.includes(tool.id)) &&
                    tool.available && 
                    tool.visibility === 'public'
                )
                .sort((a, b) => {
                    // Featured first
                    if (a.is_featured && !b.is_featured) return -1;
                    if (!a.is_featured && b.is_featured) return 1;
                    // Then high-value tools
                    const aIsHighValue = highValueToolIds.includes(a.id);
                    const bIsHighValue = highValueToolIds.includes(b.id);
                    if (aIsHighValue && !bIsHighValue) return -1;
                    if (!aIsHighValue && bIsHighValue) return 1;
                    return 0;
                })
                .slice(0, maxItems);
        }
        
        return TOOLS_CONFIG
            .filter(tool => 
                tool.category === category && 
                tool.available && 
                tool.visibility === 'public'
            )
            .sort((a, b) => {
                if (a.is_featured && !b.is_featured) return -1;
                if (!a.is_featured && b.is_featured) return 1;
                return 0;
            })
            .slice(0, maxItems);
    };
    
    const categories = [
        { label: 'Developer', category: 'Developer Tools', icon: CodeIcon },
        { label: 'Media', category: 'Media Tools', icon: ImageIcon },
        { label: 'Marketing', category: 'Digital Marketing', icon: TrendingUpIcon },
        { label: 'Calculators', category: 'Online Calculators', icon: CalculateIcon },
        { label: 'General', category: 'General', icon: WidgetsIcon, isMisc: true },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2 }}>
            {/* Header with Close Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                    Menu
                </Typography>
                <IconButton
                    onClick={handleClose}
                    edge="end"
                    aria-label="close menu"
                    sx={{ color: 'text.secondary' }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Menu Items */}
            <Box sx={{ flex: 1 }}>
                <List>
                    {/* User Info Section (if logged in) */}
                    {isLoggedIn && user && (
                        <>
                            <ListItem disablePadding sx={{ mb: 2 }}>
                                <MuiBox
                                    onClick={() => dispatch(setActiveMenu('profile'))}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        width: '100%',
                                        p: 2,
                                        borderRadius: theme.spacing(1),
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.grey[800]
                                            : theme.palette.grey[100],
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? theme.palette.grey[700]
                                                : theme.palette.grey[200],
                                        },
                                    }}
                                >
                                    <Avatar
                                        src={avatarUrl}
                                        sx={{ width: 48, height: 48 }}
                                    >
                                        {!avatarUrl && userInitials}
                                    </Avatar>
                                    <MuiBox sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={theme.typography.fontWeightBold}
                                            sx={{ mb: 0.5 }}
                                            noWrap
                                        >
                                            {userName || 'User'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {userEmail}
                                        </Typography>
                                    </MuiBox>
                                </MuiBox>
                            </ListItem>
                            <Divider sx={{ my: 2 }} />
                        </>
                    )}

                    {/* Category Menus */}
                    {categories.map((cat) => {
                        const CategoryIcon = cat.icon;
                        const tools = getCategoryTools(cat.category, 5, cat.isMisc);
                        const isOpen = openCategories[cat.category];
                        
                        if (tools.length === 0) return null;
                        
                        return (
                            <React.Fragment key={cat.category}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleCategoryToggle(cat.category)}
                                        sx={{
                                            borderRadius: theme.spacing(1),
                                            mb: 0.5,
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        <ListItemIcon>
                                            <CategoryIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={cat.label}
                                            primaryTypographyProps={{
                                                fontWeight: theme.typography.fontWeightMedium,
                                            }}
                                        />
                                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {tools.map((tool) => {
                                            const ToolIcon = tool.icon;
                                            return (
                                                <ListItem key={tool.id} disablePadding sx={{ pl: 4 }}>
                                                    <ListItemButton
                                                        onClick={() => {
                                                            navigate(tool.route);
                                                            handleClose();
                                                        }}
                                                        sx={{
                                                            borderRadius: theme.spacing(0.5),
                                                            mb: 0.5,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover,
                                                            },
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <ToolIcon sx={{ fontSize: '1.1rem' }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={tool.title}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.875rem',
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                        <ListItem disablePadding sx={{ pl: 4 }}>
                                            <ListItemButton
                                                onClick={() => {
                                                    navigate(`/catalog?category=${encodeURIComponent(cat.category)}`);
                                                    handleClose();
                                                }}
                                                sx={{
                                                    borderRadius: theme.spacing(0.5),
                                                    mb: 0.5,
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? theme.palette.primary.dark + '20'
                                                        : theme.palette.primary.light + '20',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.mode === 'dark'
                                                            ? theme.palette.primary.dark + '30'
                                                            : theme.palette.primary.light + '30',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <AppsIcon sx={{ fontSize: '1.1rem', color: theme.palette.primary.main }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="View All"
                                                    primaryTypographyProps={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: theme.typography.fontWeightMedium,
                                                        color: theme.palette.primary.main,
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        );
                    })}

                    <Divider sx={{ my: 2 }} />

                    {/* Dark Mode Toggle */}
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleThemeToggle}
                            sx={{
                                borderRadius: theme.spacing(1),
                                mb: 1,
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            <ListItemIcon>
                                {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                            </ListItemIcon>
                            <ListItemText
                                primary={mode === 'light' ? 'Dark Mode' : 'Light Mode'}
                                primaryTypographyProps={{
                                    fontWeight: theme.typography.fontWeightMedium,
                                }}
                            />
                        </ListItemButton>
                    </ListItem>

                    {/* Logout (if logged in) */}
                    {isLoggedIn && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleLogoutClick}
                                    sx={{
                                        borderRadius: theme.spacing(1),
                                        color: theme.palette.error.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? theme.palette.error.dark + '20'
                                                : theme.palette.error.light + '20',
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        <LogoutIcon sx={{ color: theme.palette.error.main }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Logout"
                                        primaryTypographyProps={{
                                            fontWeight: theme.typography.fontWeightMedium,
                                            color: theme.palette.error.main,
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </>
                    )}
                </List>
            </Box>
        </Box>
    );
};

export const AppBarPublic = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { mode, toggleMode } = useThemeMode();
    const { isLoggedIn } = useSelector((store) => store.auth);
    const activeMenu = useSelector((store) => store.app?.menu?.activeMenu);

    const appBarRef = useRef(null)

    useEffect(() => {
        dispatch(setAppBarHeight(appBarRef.current.clientHeight));
    }, [dispatch])

    const handleMobileMenuClick = () => {
        dispatch(setModal({
            component: <MobileMenuContent />,
            open: true,
            fullScreen: true,
        }));
    };

    return (
        <AppBar position="fixed" ref={appBarRef} elevation={3}>
            <Container
                sx={{
                    maxWidth: {
                        xs: "100vw",
                    },
                    minWidth: {
                        xs: "320px",
                        sm: "540px",
                        md: "720px",
                        lg: "960px",
                        xl: "1320px",
                    },
                    px: 2,
                    py: 1,
                }}
            >
                <Toolbar disableGutters>
                    {/* Logo - Left */}
                    <Box component={Link} onClick={() => dispatch(setActiveMenu('home'))} to="/" sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                        <Box
                            component="img"
                            src="/images/logo.svg"
                            alt={'DGTIA TOOLS'}
                            sx={{ height: 48 }}
                        />
                    </Box>

                    {/* Category Menus - Desktop Only */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 3 }}>
                            <CategoryMenu 
                                category="Developer Tools"
                                label="Developer"
                                icon={CodeIcon}
                                maxItems={5}
                            />
                            <CategoryMenu 
                                category="Media Tools"
                                label="Media"
                                icon={ImageIcon}
                                maxItems={5}
                            />
                            <CategoryMenu 
                                category="Digital Marketing"
                                label="Marketing"
                                icon={TrendingUpIcon}
                                maxItems={5}
                            />
                            <CategoryMenu 
                                category="Online Calculators"
                                label="Calculators"
                                icon={CalculateIcon}
                                maxItems={5}
                            />
                            <CategoryMenu 
                                category="General"
                                label="General"
                                icon={WidgetsIcon}
                                maxItems={6}
                                isMisc={true}
                            />
                        </Box>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Right Side - Search + Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Search Menu - Responsive (Desktop: inline, Mobile: icon) */}
                        <SearchMenu
                            placeholder="Search tools..."
                            width="300px"
                            minWidth="200px"
                        />

                        {/* Mobile View: Catalog Icon + Burger Menu */}
                        {isMobile ? (
                            <>
                                <AnimatedAppButton
                                    component={Link}
                                    to="/catalog"
                                    aria-label="Tools Catalog"
                                    active={activeMenu === 'catalog'}
                                    onClick={() => dispatch(setActiveMenu('catalog'))}
                                >
                                    <AppsIcon />
                                </AnimatedAppButton>
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    aria-label="menu"
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        color: 'text.secondary',
                                        border: '2px solid',
                                        borderColor: 'text.secondary',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderColor: 'text.primary',
                                            color: 'text.primary',
                                        },
                                    }}
                                >
                                    <MenuIcon sx={{ fontSize: '1.25rem' }} />
                                </IconButton>
                            </>
                        ) : (
                            /* Desktop View: All Icons Visible */
                            <Stack direction={"row"} spacing={2} alignItems="center">
                                <AnimatedAppButton
                                    component={Link}
                                    to="/catalog"
                                    aria-label="Tools Catalog"
                                    active={activeMenu === 'catalog'}
                                    onClick={() => dispatch(setActiveMenu('catalog'))}
                                >
                                    <AppsIcon />
                                </AnimatedAppButton>
                                <StyledThemeToggle
                                    onClick={() => {
                                        toggleMode();
                                    }}
                                    aria-label="toggle theme"
                                >
                                    {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                                </StyledThemeToggle>
                                {isLoggedIn && <AccountMenu />}
                            </Stack>
                        )}
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    )
}